import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ClipboardList,
  Clock,
  XCircle,
  PoundSterling,
  Timer,
  FileEdit,
  Sparkles,
} from 'lucide-react';
import type { TimesheetReportFilters } from '@/types/timesheetReport';
import { startOfWeek, endOfWeek, startOfMonth, endOfMonth, subWeeks, subMonths } from 'date-fns';

export interface FilterTemplate {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  filters: TimesheetReportFilters;
  color: string;
}

interface QuickTemplatesProps {
  onApplyTemplate: (template: FilterTemplate) => void;
  currentUserId?: string;
}

export const QuickTemplates: React.FC<QuickTemplatesProps> = ({
  onApplyTemplate,
  currentUserId,
}) => {
  // Define quick filter templates
  const templates: FilterTemplate[] = [
    {
      id: 'pending-approvals',
      name: 'My Pending Approvals',
      description: 'Submitted timesheets awaiting approval',
      icon: <ClipboardList className="h-4 w-4" />,
      color: 'bg-blue-50 text-blue-700 hover:bg-blue-100 border-blue-200',
      filters: {
        statuses: ['submitted'],
        dateFrom: startOfWeek(new Date(), { weekStartsOn: 0 }),
        dateTo: endOfWeek(new Date(), { weekStartsOn: 0 }),
        quickDateRange: 'this-week',
      },
    },
    {
      id: 'late-submissions',
      name: 'Late Submissions',
      description: 'Timesheets submitted after deadline',
      icon: <Clock className="h-4 w-4" />,
      color: 'bg-orange-50 text-orange-700 hover:bg-orange-100 border-orange-200',
      filters: {
        statuses: ['submitted'],
        dateFrom: subWeeks(startOfWeek(new Date(), { weekStartsOn: 0 }), 1),
        dateTo: subWeeks(endOfWeek(new Date(), { weekStartsOn: 0 }), 1),
        quickDateRange: 'last-week',
      },
    },
    {
      id: 'rejected-month',
      name: 'Rejected This Month',
      description: 'Timesheets rejected in current month',
      icon: <XCircle className="h-4 w-4" />,
      color: 'bg-red-50 text-red-700 hover:bg-red-100 border-red-200',
      filters: {
        statuses: ['rejected'],
        rejectedOnly: true,
        dateFrom: startOfMonth(new Date()),
        dateTo: endOfMonth(new Date()),
        quickDateRange: 'this-month',
      },
    },
    {
      id: 'billable-hours',
      name: 'Billable Hours Report',
      description: 'Approved billable hours for client billing',
      icon: <PoundSterling className="h-4 w-4" />,
      color: 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border-emerald-200',
      filters: {
        statuses: ['approved'],
        billableOnly: true,
        dateFrom: startOfMonth(new Date()),
        dateTo: endOfMonth(new Date()),
        quickDateRange: 'this-month',
      },
    },
    {
      id: 'team-overtime',
      name: 'Team Overtime',
      description: 'Timesheets with over 40 hours this week',
      icon: <Timer className="h-4 w-4" />,
      color: 'bg-purple-50 text-purple-700 hover:bg-purple-100 border-purple-200',
      filters: {
        statuses: ['submitted', 'approved'],
        dateFrom: startOfWeek(new Date(), { weekStartsOn: 0 }),
        dateTo: endOfWeek(new Date(), { weekStartsOn: 0 }),
        quickDateRange: 'this-week',
        // Note: Hours filtering would need backend support
      },
    },
    {
      id: 'incomplete-timesheets',
      name: 'Incomplete Timesheets',
      description: 'Draft timesheets from last week',
      icon: <FileEdit className="h-4 w-4" />,
      color: 'bg-amber-50 text-amber-700 hover:bg-amber-100 border-amber-200',
      filters: {
        statuses: ['draft'],
        dateFrom: subWeeks(startOfWeek(new Date(), { weekStartsOn: 0 }), 1),
        dateTo: subWeeks(endOfWeek(new Date(), { weekStartsOn: 0 }), 1),
        quickDateRange: 'last-week',
      },
    },
  ];

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-blue-600" />
        <h3 className="text-sm font-medium">Quick Templates</h3>
        <Badge variant="secondary" className="h-5 text-xs">
          One-Click
        </Badge>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {templates.map((template) => (
          <Button
            key={template.id}
            variant="outline"
            className={`h-auto p-3 justify-start text-left border ${template.color}`}
            onClick={() => onApplyTemplate(template)}
          >
            <div className="flex items-start gap-3 w-full">
              <div className="flex-shrink-0 mt-0.5">{template.icon}</div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm">{template.name}</div>
                <div className="text-xs opacity-80 mt-0.5 line-clamp-2">
                  {template.description}
                </div>
              </div>
            </div>
          </Button>
        ))}
      </div>

      <div className="text-xs text-gray-500 flex items-center gap-1 pt-1">
        <Sparkles className="h-3 w-3" />
        Click any template to instantly apply its filters
      </div>
    </div>
  );
};
